import { Suspense } from 'react';
import { graphql } from 'gatsby';
import { RichTextElement } from '@kontent-ai/gatsby-components';

import { applyPaddingCSS } from '@/utils/generatePadding';
import { type BlockHTMLId } from '@/utils/renderBlock';
import StarRating from '@/components/StarRating/StarRating';
import InfoTagGroup from '@/components/InfoTagGroup/InfoTagGroup';
import Link from '@/components/Link/Link';
import { StyledLogoBlock } from './components/LogoBlock.styles';
import { StyledCarouselSection } from './components/CarouselSection.styles';
import { StyledStatBarBlock } from './components/StatBarBlock.styles';
import { StyledAnnouncementButton } from './components/AnnoucementButton.styles';
import { StyledLinkGroup } from './components/LinkGroup.styles';
import { isRichTextValid } from '../RichTextBlock/utils';

import {
  StyledContainer,
  StyledSection,
  StyledImageContainer,
  StyledImage,
  StyledContent,
  StyledSideRichText,
  StyledRichText,
  StyledSideContainer,
} from './SuperCTABlock.styles';

type Items =
  | Queries.ANNOUNCEMENT_BUTTONFragment
  | Queries.CAROUSEL_SECTIONFragment
  | Queries.INFO_TAG_GROUPFragment
  | Queries.LINK_GROUPFragment
  | Queries.LOGO_BLOCKFragment
  | Queries.STAR_RATINGFragment
  | Queries.STATBAR_BLOCKFragment;

const DEFAULT_IMAGE_SIZE = 400;

const resolveLinkedItems = (item: Items, hasSideContent: boolean = false) => {
  switch (item?.__typename) {
    case 'kontent_item_announcement_button':
      return <Suspense>{<StyledAnnouncementButton {...item} />}</Suspense>;
    case 'kontent_item_carousel_section':
      return (
        <Suspense>{<StyledCarouselSection {...item} hasSideContent={hasSideContent} />}</Suspense>
      );
    case 'kontent_item_info_tag_group':
      return <Suspense>{<InfoTagGroup {...item} />}</Suspense>;
    case 'kontent_item_link_group':
      return <Suspense>{<StyledLinkGroup {...item} />}</Suspense>;
    case 'kontent_item_logo_block':
      return (
        <Suspense>
          {
            <StyledLogoBlock
              {...item}
              logoBlockElements={{
                ...item.logoBlockElements,
                mode: { value: 'condensed' },
              }}
              hasSideContent={hasSideContent}
            />
          }
        </Suspense>
      );
    case 'kontent_item_star_rating':
      return <Suspense>{<StarRating {...item} />}</Suspense>;
    case 'kontent_item_statbar_block':
      return (
        <Suspense>{<StyledStatBarBlock {...item} hasSideContent={hasSideContent} />}</Suspense>
      );
    default:
      return null;
  }
};

const resolveSideLinkedItems = (item: Queries.LINKFragment) => {
  switch (item?.__typename) {
    case 'kontent_item_link':
      return <Suspense>{<Link {...item} variant="tertiary" />}</Suspense>;
    default:
      return null;
  }
};

const SuperCTABlock = ({
  superCTABlockElements: elements,
  system,
  id,
}: Queries.SUPER_CTA_BLOCKFragment & BlockHTMLId) => {
  let { content, background_color, image, image_option, side_text } = elements;
  const hasContent = isRichTextValid(content?.value);
  // NOTE: Give priority to side content if both image and side content are present
  const hasSideText = !!side_text && isRichTextValid(side_text?.value);
  const hasImage = !!image?.value && !hasSideText;
  const isBackgroundImage = hasImage && image_option?.value !== 'square_400x400';
  const hasSideContent = hasSideText || (hasImage && image_option?.value !== 'background_fill');

  return (
    <StyledSection
      data-codename={system.codename}
      css={applyPaddingCSS(elements)}
      id={id}
      backgroundColor={background_color}
    >
      <StyledContainer imageOption={image_option} hasImage={hasImage} hasSideText={hasSideText}>
        <StyledContent>
          {hasContent && (
            <StyledRichText>
              <RichTextElement
                linkedItems={content.modular_content as any[]}
                resolveLinkedItem={(item: Items) => resolveLinkedItems(item, hasSideContent)}
                value={content.value}
                // @ts-expect-error images is readonly thanks to the graphql types...
                images={content.images}
              />
            </StyledRichText>
          )}
        </StyledContent>
        {hasImage && (
          <StyledImageContainer image={isBackgroundImage ? image : undefined}>
            {!isBackgroundImage && (
              <StyledImage
                src={image.value.url}
                alt={image.value.description ?? ''}
                width={DEFAULT_IMAGE_SIZE}
                height={DEFAULT_IMAGE_SIZE}
              />
            )}
          </StyledImageContainer>
        )}
        {hasSideText && (
          <StyledSideContainer>
            <StyledSideRichText>
              <RichTextElement
                linkedItems={side_text.modular_content as any[]}
                resolveLinkedItem={resolveSideLinkedItems}
                value={side_text.value}
              />
            </StyledSideRichText>
          </StyledSideContainer>
        )}
      </StyledContainer>
    </StyledSection>
  );
};

export default SuperCTABlock;

export const fragment = graphql`
  fragment SUPER_CTA_BLOCK on kontent_item_super_cta_block {
    superCTABlockElements: elements {
      ...COMPONENT_A_B_TEST
      ...COMPONENT_PERSONALIZATION
      ...PADDING
      content {
        value
        modular_content {
          ...STAR_RATING
          ...INFO_TAG_GROUP
          ...LINK_GROUP
          ...ANNOUNCEMENT_BUTTON
          ...STATBAR_BLOCK
          ...CAROUSEL_SECTION
          ...LOGO_BLOCK
        }
        images {
          description
          height
          url
          image_id
          width
        }
      }
      background_color {
        value
      }
      image {
        value {
          description
          url
          width
          height
        }
      }
      image_option {
        value
      }
      side_text {
        value
        modular_content {
          ...LINK
        }
      }
    }
    system {
      codename
      type
      id
    }
  }
`;
